import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { landlordsAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Plus,
  UserCircle,
  Edit,
  Trash2,
  Loader2,
  Search,
  Mail,
  Phone,
  MapPin,
  Building
} from 'lucide-react';

export default function Landlords() {
  const [landlords, setLandlords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLandlord, setEditingLandlord] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    address: '',
    postal_code: '',
    city: '',
    email: '',
    phone: '',
    bank_name: '',
    iban: '',
    bic: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadLandlords();
  }, []);

  const loadLandlords = async () => {
    try {
      const response = await landlordsAPI.getAll();
      setLandlords(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Vermieter');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      company: '',
      address: '',
      postal_code: '',
      city: '',
      email: '',
      phone: '',
      bank_name: '',
      iban: '',
      bic: ''
    });
    setEditingLandlord(null);
  };

  const openEditDialog = (landlord) => {
    setEditingLandlord(landlord);
    setFormData({
      name: landlord.name,
      company: landlord.company || '',
      address: landlord.address,
      postal_code: landlord.postal_code,
      city: landlord.city,
      email: landlord.email || '',
      phone: landlord.phone || '',
      bank_name: landlord.bank_name || '',
      iban: landlord.iban || '',
      bic: landlord.bic || ''
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        ...formData,
        company: formData.company || null,
        email: formData.email || null,
        phone: formData.phone || null,
        bank_name: formData.bank_name || null,
        iban: formData.iban || null,
        bic: formData.bic || null
      };

      if (editingLandlord) {
        await landlordsAPI.update(editingLandlord.id, data);
        toast.success('Vermieter aktualisiert');
      } else {
        await landlordsAPI.create(data);
        toast.success('Vermieter erstellt');
      }

      setIsDialogOpen(false);
      resetForm();
      loadLandlords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await landlordsAPI.delete(deleteId);
      toast.success('Vermieter gelöscht');
      loadLandlords();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    } finally {
      setDeleteId(null);
    }
  };

  const filteredLandlords = landlords.filter(l =>
    l.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (l.company && l.company.toLowerCase().includes(searchTerm.toLowerCase())) ||
    l.city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="landlords-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Vermieter
            </h1>
            <p className="text-slate-500 mt-1">Verwalten Sie Ihre Vermieter</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-landlord-button" className="bg-slate-900 hover:bg-slate-800">
                <Plus className="mr-2 h-4 w-4" />
                Vermieter hinzufügen
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingLandlord ? 'Vermieter bearbeiten' : 'Neuer Vermieter'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="name">
                      Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      data-testid="landlord-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Max Mustermann"
                      required
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="company">Firma (optional)</Label>
                    <Input
                      id="company"
                      data-testid="landlord-company-input"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      placeholder="Mustermann Immobilien GmbH"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="address">
                      Straße & Hausnummer <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="address"
                      data-testid="landlord-address-input"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Musterstraße 1"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="postal_code">
                      PLZ <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="postal_code"
                      data-testid="landlord-postal-code-input"
                      value={formData.postal_code}
                      onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                      placeholder="12345"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">
                      Stadt <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="city"
                      data-testid="landlord-city-input"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="Berlin"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">E-Mail</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="landlord-email-input"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="vermieter@email.de"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefon</Label>
                    <Input
                      id="phone"
                      data-testid="landlord-phone-input"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+49 123 456789"
                    />
                  </div>
                </div>

                {/* Bank Details Section */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium text-slate-900 mb-3">Bankverbindung</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="bank_name">Bank</Label>
                      <Input
                        id="bank_name"
                        data-testid="landlord-bank-input"
                        value={formData.bank_name}
                        onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                        placeholder="Sparkasse Berlin"
                      />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="iban">IBAN</Label>
                      <Input
                        id="iban"
                        data-testid="landlord-iban-input"
                        value={formData.iban}
                        onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                        placeholder="DE89 3704 0044 0532 0130 00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="bic">BIC</Label>
                      <Input
                        id="bic"
                        data-testid="landlord-bic-input"
                        value={formData.bic}
                        onChange={(e) => setFormData({ ...formData, bic: e.target.value })}
                        placeholder="COBADEFFXXX"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Abbrechen
                  </Button>
                  <Button type="submit" disabled={saving} data-testid="landlord-save-button">
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Speichern...
                      </>
                    ) : (
                      'Speichern'
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="landlord-search-input"
            placeholder="Suchen nach Name, Firma oder Stadt..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Landlords List */}
        {filteredLandlords.length > 0 ? (
          <Card className="border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="font-semibold">Vermieter</TableHead>
                  <TableHead className="font-semibold">Adresse</TableHead>
                  <TableHead className="font-semibold">Kontakt</TableHead>
                  <TableHead className="font-semibold">Bankverbindung</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLandlords.map((landlord) => (
                  <TableRow key={landlord.id} data-testid={`landlord-row-${landlord.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                          <UserCircle className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{landlord.name}</p>
                          {landlord.company && (
                            <p className="text-sm text-slate-500 flex items-center gap-1">
                              <Building className="h-3 w-3" />
                              {landlord.company}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-slate-700">{landlord.address}</p>
                          <p className="text-sm text-slate-500">
                            {landlord.postal_code} {landlord.city}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {landlord.email && (
                          <p className="text-sm text-slate-600 flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {landlord.email}
                          </p>
                        )}
                        {landlord.phone && (
                          <p className="text-sm text-slate-600 flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {landlord.phone}
                          </p>
                        )}
                        {!landlord.email && !landlord.phone && (
                          <span className="text-slate-400">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {landlord.iban ? (
                        <div>
                          <p className="text-sm text-slate-700 font-mono">{landlord.iban}</p>
                          {landlord.bank_name && (
                            <p className="text-xs text-slate-500">{landlord.bank_name}</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`edit-landlord-${landlord.id}`}
                          onClick={() => openEditDialog(landlord)}
                        >
                          <Edit className="h-4 w-4 text-slate-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`delete-landlord-${landlord.id}`}
                          onClick={() => setDeleteId(landlord.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        ) : (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <UserCircle className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {searchTerm ? 'Keine Vermieter gefunden' : 'Noch keine Vermieter'}
              </h3>
              <p className="text-slate-500 mb-4">
                {searchTerm 
                  ? 'Versuchen Sie einen anderen Suchbegriff' 
                  : 'Fügen Sie Ihren ersten Vermieter hinzu'
                }
              </p>
              {!searchTerm && (
                <Button onClick={() => setIsDialogOpen(true)} data-testid="empty-add-landlord-button">
                  <Plus className="mr-2 h-4 w-4" />
                  Ersten Vermieter hinzufügen
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Vermieter löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Diese Aktion kann nicht rückgängig gemacht werden. Die Verknüpfung 
                zu den Einheiten wird entfernt.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-landlord"
              >
                Löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
